import {
    computed,
    Directive,
    effect,
    inject,
    InjectionToken,
    input,
    output,
    type InputSignal,
    type OutputEmitterRef,
    type Type,
    ViewContainerRef,
} from '@angular/core';
import {
    injectQuippySession,
    type QuippyChatRef,
    type QuippyScope,
} from '../runtime';

export interface QuippyPendingChat {
    readonly ref: QuippyChatRef;
    readonly prompt: {
        readonly kind: 'draft' | 'submit';
        readonly text: string;
    } | null;
}

export interface QuippyChatHandoff {
    consume(scope: QuippyScope): QuippyPendingChat | null;
}

export const QUIPPY_CHAT_HANDOFF = new InjectionToken<QuippyChatHandoff>(
    'QUIPPY_CHAT_HANDOFF'
);

export interface QuippyChatEntry {
    navigate(request: {
        readonly target: QuippyScope;
        readonly prompt?: {
            readonly kind: 'draft' | 'submit';
            readonly text: string;
        };
    }): Promise<boolean>;
}

export const QUIPPY_CHAT_ENTRY = new InjectionToken<QuippyChatEntry>(
    'QUIPPY_CHAT_ENTRY'
);

export interface QuippyHomeActions {
    draft(scope: QuippyScope): string;
    setDraft(scope: QuippyScope, text: string): void;
    navigateHome(scope: QuippyScope): Promise<boolean>;
}

export const QUIPPY_HOME_ACTIONS = new InjectionToken<QuippyHomeActions>(
    'QUIPPY_HOME_ACTIONS'
);

/** Navigation and presentation decisions supplied by the authenticated app shell. */
export interface QuippyHostActions {
    openPack?(buildingId: string, packId?: string): Promise<boolean>;
    navigateToChat(ref: QuippyChatRef): Promise<boolean>;
    openDialog(ref: QuippyChatRef): Promise<void>;
    newPageChat(scope: QuippyScope, ref?: QuippyChatRef): Promise<boolean>;
    takeNewPageRef(scope: QuippyScope): QuippyChatRef | null;
    replaceNewRoute(ref: QuippyChatRef): Promise<boolean>;
}

export const QUIPPY_HOST_ACTIONS = new InjectionToken<QuippyHostActions>(
    'QUIPPY_HOST_ACTIONS'
);

export type QuippyScopeSelection =
    | { readonly kind: 'portfolio' }
    | {
          readonly kind: 'building';
          readonly building: {
              readonly id: string;
              readonly name: string;
              readonly address: string;
          };
      };

/** The app shell supplies the building picker without coupling Quippy to building features. */
export interface QuippyScopePickerAdapter {
    readonly selection: InputSignal<QuippyScopeSelection>;
    readonly selectionChange: OutputEmitterRef<QuippyScopeSelection>;
}

export const QUIPPY_SCOPE_PICKER_COMPONENT = new InjectionToken<
    Type<QuippyScopePickerAdapter>
>('QUIPPY_SCOPE_PICKER_COMPONENT');

@Directive({ selector: '[qpxQuippyScopePickerHost]' })
export class QuippyScopePickerHost {
    public readonly scope = input.required<QuippyScope>();
    public readonly scopeChange = output<QuippyScope>();

    private readonly session = injectQuippySession();
    private readonly component = inject(ViewContainerRef).createComponent(
        inject(QUIPPY_SCOPE_PICKER_COMPONENT)
    );
    private readonly selection = computed<QuippyScopeSelection>(() => {
        const scope = this.scope();
        if (scope.kind === 'portfolio') return scope;

        const details = this.session.buildingDetails(scope);
        return {
            kind: 'building',
            building: {
                id: scope.buildingId,
                name: details?.name ?? 'Selected building',
                address: details?.address ?? '',
            },
        };
    });

    public constructor() {
        this.component.instance.selectionChange.subscribe((selection) => {
            if (selection.kind === 'portfolio') {
                this.scopeChange.emit(selection);
                return;
            }
            const scope: QuippyScope = {
                kind: 'building',
                buildingId: selection.building.id,
            };
            this.session.updateBuilding(scope, {
                name: selection.building.name,
                address: selection.building.address,
            });
            this.scopeChange.emit(scope);
        });
        effect(() => this.component.setInput('selection', this.selection()));
    }
}
